// SequentialCommandsModal.js
import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import DraggableModal from '../DraggableModal';
import { makeApiRequest } from '../../../api';
import { ButtonPrimary } from '../../Buttons';

export default function SequentialCommandsModal({
  isOpen,
  setIsOpen,
  startPosition,
  onAddCommandToCodeClicked,
}) {
  const [commandsInfo, setCommandsInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openCommand, setOpenCommand] = useState(null); // To track which command is expanded

  const fetchCommands = async () => {
    try {
      setLoading(true);
      const data = await makeApiRequest('/commands/', 'GET');

      // Sort the data by keys (command names) alphabetically
      const sortedData = Object.keys(data)
        .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
        .reduce((result, key) => {
          result[key] = data[key];
          return result;
        }, {});

      setCommandsInfo(sortedData);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setError('Failed to load commands information.');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchCommands();
    }
  }, [isOpen]);

  const toggleCommand = (commandName) => {
    setOpenCommand((prev) => (prev === commandName ? null : commandName));
  };

  const generateUsageString = (command) => {
    const requiredArgs = command.required_args
      .map((arg) => `[${arg.arg_name}: ${arg.arg_type}]`)
      .join(' ');
    const optionalArgs = command.optional_args
      .map((arg) => `(${arg.arg_name}: ${arg.arg_type})`)
      .join(' ');
    return `${command.name} ${requiredArgs} ${optionalArgs}`.trim();
  };

  return (
    <DraggableModal
      title="Sequential Commands Guide"
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      startPosition={startPosition}
      className={"w-[500px]"}
    >
      {loading ? (
        <p className="text-center">Loading...</p>
      ) : error ? (
        <p className="text-center text-red-500">{error}</p>
      ) : (
        <div className="h-[500px] overflow-y-auto overflow-x-hidden space-y-6 w-full">
          {Object.keys(commandsInfo).map((commandName) => {
            const command = commandsInfo[commandName];
            const isOpen = openCommand === commandName;
            const usageString = generateUsageString(command);

            return (
              <div
                key={commandName}
                className="p-4 bg-gray-100 rounded-md shadow-md"
              >
                <strong
                  onClick={() => toggleCommand(commandName)}
                  className="cursor-pointer text-primary text-lg"
                >
                  {command.name}
                </strong>
                {isOpen && (
                  <div className="mt-4">
                    <p className="font-semibold bg-gray-200 p-2 rounded-md">
                      <span className="text-primary font-bold">Usage:</span>{' '}
                      {usageString}
                    </p>
                    <h4 className="mt-4 font-semibold border-b pb-2">
                      Description
                    </h4>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm, remarkBreaks]}
                      className="mt-2 whitespace-pre-wrap break-words overflow-x-hidden w-full"
                        components={{
                          code({ node, inline, className, children, ...props }) {
                            const match = /language-(\w+)/.exec(className || '');
                            return !inline && match ? (
                            <pre className="bg-gray-800 rounded-md p-4 whitespace-pre-wrap break-words overflow-x-hidden">
                                <code
                                className={`language-${match[1]} text-white break-words whitespace-pre-wrap`}
                                  {...props}
                                >
                                  {String(children).replace(/\n$/, '')}
                                </code>
                              </pre>
                            ) : (
                              <code
                              className="bg-gray-200 p-1 rounded break-words whitespace-pre-wrap"
                                {...props}
                              >
                                {children}
                              </code>
                            );
                          },
                        }}
                      >
                        {command.description}
                      </ReactMarkdown>
                    <ButtonPrimary
                      onClick={() => onAddCommandToCodeClicked(usageString)}
                      className="mt-4"
                    >
                      Add to Code
                    </ButtonPrimary>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </DraggableModal>
  );
}
